import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, Lock, Play } from "lucide-react";
import { C } from "../../theme/tokens";
import { ProtectedPdfViewer } from "../../components/education/ProtectedPdfViewer";
import {
  toEmbeddableVideoUrl,
  isIframeBlockedHost,
  getVideoThumbnail,
  openExternalLink,
} from "../../utils/video";

/* ---------------------------------------------------------------
   Resolves a short-lived, signed playback URL for a lesson's own-
   hosted video (own storage/CDN, not YouTube/Vimeo). Deliberately
   NOT fetched as bytes (like fetchLessonPdfBytes does) — lesson
   videos run ~hundreds of MB, and pulling that into an ArrayBuffer
   would both block on a full download before playback could start
   and risk blowing the WebView's memory on mobile. Instead the
   backend hands back a URL that the storage/CDN itself serves with
   Range-request support (206 Partial Content), so the <video> tag
   can stream/seek progressively without the request going through
   our own server at all.

   Backend contract this assumes (adjust the path to match your
   actual API, same pattern as the /pdf endpoint below):
   GET education/lesson/{lessonId}/video -> 200 { url, expires_at }
   with `url` pointing directly at storage/CDN (presigned S3/R2 link
   or a signed CDN token URL), or 403/404 if not unlocked / no video
   attached to this lesson yet.
------------------------------------------------------------------ */
async function fetchLessonVideoUrl(authFetch, lessonId) {
  const path = `education/lesson/${lessonId}/video`;
  const res = await authFetch(path, { method: "GET" });
  if (!res.ok) throw new Error(`lesson video API ${res.status}`);
  const data = await res.json();
  return data?.url || "";
}

/* ---------------------------------------------------------------
   Fetches the raw PDF bytes for one lesson through the app's
   existing authFetch (JWT-protected — see hooks/useAuth.js), so the
   file is never reachable as a plain static URL that could be
   shared or curled directly.

   Backend contract this assumes (adjust the path to match your
   actual API): GET education/lesson/{lessonId}/pdf returns the raw
   PDF bytes with a 200 + application/pdf, or 403/404 if the lesson
   isn't unlocked for this user. Enforcing access server-side is what
   actually matters — the client-side viewer only stops the casual
   "right click -> save as", it can't replace a real authorization
   check on the endpoint itself.
------------------------------------------------------------------ */
async function fetchLessonPdfBytes(authFetch, lessonId) {
  const path = `education/lesson/${lessonId}/pdf`;
  // eslint-disable-next-line no-console
  console.log("[LessonPdfScreen] requesting lesson PDF:", path, "authFetch is", typeof authFetch);
  const res = await authFetch(path, { method: "GET" });
  // eslint-disable-next-line no-console
  console.log("[LessonPdfScreen] lesson PDF response:", res.status, res.url);
  if (!res.ok) throw new Error(`lesson pdf API ${res.status}`);
  return res.arrayBuffer();
}

export function LessonPdfScreen({ lesson, user, authFetch, t, onBack, wide }) {
  const fetchPdfBytes = useCallback(
    () => fetchLessonPdfBytes(authFetch, lesson.id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lesson.id]
  );

  const watermarkLabel = buildWatermarkLabel(user);

  // "own" -> resolving/using a signed direct-file URL from our own
  // storage (native <video>, no iframe involved at all).
  // "legacy" -> couldn't resolve one (video endpoint 404/errored),
  // falling back to the old YouTube/Vimeo iframe-or-open-externally
  // path for lessons not yet migrated off external links.
  const [videoMode, setVideoMode] = useState(lesson.hasVideo ? "loading" : "none");
  const [ownVideoUrl, setOwnVideoUrl] = useState("");

  useEffect(() => {
    let cancelled = false;
    if (!lesson.hasVideo) {
      setVideoMode("none");
      return;
    }
    setVideoMode("loading");
    fetchLessonVideoUrl(authFetch, lesson.id)
      .then((url) => {
        if (cancelled) return;
        // Any non-empty URL from OUR OWN /video endpoint is trusted as
        // the own-storage stream link as-is — it's our own stream
        // endpoint (".../video/stream/?token=...") and deliberately has
        // no file extension, so an extension-based check would (and
        // did) wrongly reject it. isDirectVideoUrl is kept only for
        // callers who need to sniff an arbitrary/unknown URL.
        if (url) {
          setOwnVideoUrl(url);
          setVideoMode("own");
        } else {
          setVideoMode("legacy");
        }
      })
      .catch(() => {
        if (!cancelled) setVideoMode("legacy");
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lesson.id]);

  // Legacy fallback path only (external YouTube/Vimeo link, not our
  // own storage): same iframe-or-click-to-open-externally logic as
  // before, since Telegram's WebView hard-blocks iframes to those
  // hosts (shows its own "host is blocked" screen).
  const embedUrl = useMemo(
    () => (videoMode === "legacy" ? toEmbeddableVideoUrl(lesson.videoLink) : ""),
    [videoMode, lesson.videoLink]
  );
  const blockedInIframe = useMemo(() => embedUrl && isIframeBlockedHost(embedUrl), [embedUrl]);
  const thumbnail = useMemo(() => (blockedInIframe ? getVideoThumbnail(lesson.videoLink) : ""), [blockedInIframe, lesson.videoLink]);

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: wide ? "0" : "20px 16px 0",
        }}
      >
        <button
          onClick={onBack}
          style={{
            width: 34,
            height: 34,
            borderRadius: 10,
            border: `1px solid ${C.hairline}`,
            background: C.panel1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
          }}
        >
          <ArrowLeft size={16} color={C.primary} />
        </button>
        <div
          style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 600,
            fontSize: 14.5,
            color: C.primary,
            maxWidth: "62%",
            textAlign: "center",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {lesson.title}
        </div>
        <div style={{ width: 34 }} />
      </div>

      <div style={{ padding: wide ? "18px 0 0" : "18px 16px 24px", maxWidth: wide ? 860 : undefined }}>
        {(videoMode === "loading" || videoMode === "own" || (videoMode === "legacy" && embedUrl)) && (
          <div style={{ marginBottom: 18 }}>
            <div
              style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontWeight: 600,
                fontSize: 13.5,
                color: C.primary,
                marginBottom: 8,
              }}
            >
              {t?.edu_video_title || "Відео уроку"}
            </div>
            <div
              style={{
                position: "relative",
                width: "100%",
                paddingTop: "56.25%",
                borderRadius: 14,
                overflow: "hidden",
                border: `1px solid ${C.hairline}`,
                background: "#030407",
              }}
            >
              {videoMode === "loading" && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: C.secondary,
                    fontSize: 12.5,
                  }}
                >
                  {t?.lesson_loading || "Завантаження..."}
                </div>
              )}

              {videoMode === "own" && (
                // Native <video>, not an <iframe> — Telegram's Mini App
                // WebView only blocks iframes to third-party hosts, not
                // media elements, and this URL points straight at our
                // own storage/CDN (Range-request support -> progressive
                // playback + seeking without downloading the whole file
                // up front, which matters a lot for large lesson videos).
                <video
                  key={ownVideoUrl}
                  src={ownVideoUrl}
                  controls
                  playsInline
                  preload="metadata"
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    background: "#030407",
                  }}
                />
              )}

              {videoMode === "legacy" && embedUrl && (blockedInIframe ? (
                <button
                  onClick={() => openExternalLink(lesson.videoLink)}
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    background: thumbnail
                      ? `linear-gradient(0deg, rgba(0,0,0,0.35), rgba(0,0,0,0.35)), url(${thumbnail}) center / cover no-repeat`
                      : `linear-gradient(120deg, ${C.panel2}, ${C.panel1})`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <span
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: "50%",
                      background: C.eduBright,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 6px 18px rgba(0,0,0,0.35)",
                    }}
                  >
                    <Play size={22} color="#241705" fill="#241705" style={{ marginLeft: 2 }} />
                  </span>
                </button>
              ) : (
                <iframe
                  src={embedUrl}
                  title={lesson.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  frameBorder="0"
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                  }}
                />
              ))}
            </div>
          </div>
        )}

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 11.5,
            color: C.mutedB,
            marginBottom: 10,
          }}
        >
          <Lock size={12} /> {t?.lesson_protected_hint || "Матеріал доступний лише для перегляду"}
        </div>
        <ProtectedPdfViewer fetchPdfBytes={fetchPdfBytes} watermarkLabel={watermarkLabel} t={t} />
      </div>
    </div>
  );
}

function buildWatermarkLabel(user) {
  const name = user?.first_name || user?.username || "user";
  const idPart = user?.telegram_id || user?.id || "";
  const stamp = new Date().toLocaleString("uk-UA", { dateStyle: "short", timeStyle: "short" });
  return `${name} · id ${idPart} · ${stamp}`;
}
